import React, { useEffect, useState } from 'react';
import { CalendarCheck, Heart, Loader2, PackageCheck, UserCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import AccountLayout from '../../components/AccountLayout';
import { getAccountDashboard, ROLE_LABELS } from '../../config';

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function HistoryTable({ title, icon: Icon, items, emptyLabel, type }) {
  return (
    <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-200">
        <Icon className="w-4 h-4 text-secondary" />
        <h2 className="font-heading text-base font-semibold text-primary uppercase tracking-wider">{title}</h2>
      </div>

      {items.length === 0 ? (
        <div className="p-6 text-sm text-slate-500">{emptyLabel}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left px-5 py-3 text-xs text-slate-600 font-medium">Reference</th>
                <th className="text-left px-5 py-3 text-xs text-slate-600 font-medium">Resume</th>
                <th className="text-left px-5 py-3 text-xs text-slate-600 font-medium">Statut</th>
                <th className="text-left px-5 py-3 text-xs text-slate-600 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-slate-200 last:border-0">
                  <td className="px-5 py-3 text-primary font-medium">
                    {type === 'order' ? item.reference : item.id}
                  </td>
                  <td className="px-5 py-3 text-slate-700">
                    {item.product_id ? (
                      <Link to={`/produit/${item.product_id}`} className="hover:text-secondary transition-colors">
                        {item.summary || item.product_name}
                      </Link>
                    ) : (
                      item.summary || item.product_name || '-'
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-700">{item.status || '-'}</td>
                  <td className="px-5 py-3 text-slate-600">{formatDate(item.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AccountDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const response = await getAccountDashboard();
        if (!active) return;
        setData(response);
      } catch {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <AccountLayout title="Tableau de bord">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-secondary animate-spin" />
        </div>
      </AccountLayout>
    );
  }

  const user = data?.user;
  const reservations = data?.reservations || [];
  const orders = data?.orders || [];
  const wishlistCount = data?.wishlist_count || 0;

  return (
    <AccountLayout title="Tableau de bord">
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
          <div className="bg-white rounded-sm border border-slate-200 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-secondary/15 flex items-center justify-center">
                <UserCircle2 className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-semibold text-primary">{user?.name}</h2>
                <p className="text-sm text-slate-600">{ROLE_LABELS[user?.role] || user?.role}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 rounded-sm border border-slate-200 p-4">
                <p className="text-xs text-slate-500 mb-1">Email</p>
                <p className="text-primary break-all">{user?.email}</p>
              </div>
              <div className="bg-slate-50 rounded-sm border border-slate-200 p-4">
                <p className="text-xs text-slate-500 mb-1">Telephone</p>
                <p className="text-primary">{user?.phone || '-'}</p>
              </div>
              <div className="bg-slate-50 rounded-sm border border-slate-200 p-4 sm:col-span-2">
                <p className="text-xs text-slate-500 mb-1">Adresse</p>
                <p className="text-primary">{[user?.address, user?.postal_code, user?.city, user?.country].filter(Boolean).join(', ') || '-'}
                </p>
              </div>
            </div>

            <Link
              to="/compte/profil"
              className="inline-flex items-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-5 py-2.5 text-sm transition-colors"
            >
              Mettre a jour mon profil
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {[
              { label: 'Reservations recentes', value: reservations.length, icon: CalendarCheck },
              { label: 'Commandes recentes', value: orders.length, icon: PackageCheck },
              { label: 'Favoris enregistres', value: wishlistCount, icon: Heart },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-sm border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-slate-600">{item.label}</span>
                  <item.icon className="w-4 h-4 text-secondary" />
                </div>
                <p className="font-heading text-2xl font-bold text-primary">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        <HistoryTable
          title="Dernieres reservations"
          icon={CalendarCheck}
          items={reservations}
          emptyLabel="Aucune reservation liee a ce compte pour le moment."
          type="reservation"
        />

        <HistoryTable
          title="Dernieres commandes"
          icon={PackageCheck}
          items={orders}
          emptyLabel="Aucune commande enregistree pour le moment."
          type="order"
        />
      </div>
    </AccountLayout>
  );
}
